import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchFiles } from '../contexts/SearchFilesContext';
import Content from '../components/Content';
import SearchInterface from '../components/SearchInterface';
import SearchSummary from '../components/SearchSummary';

function Research() {
  const { getSelectedFiles, updateSelectedFiles, librarySearchState, setLibrarySearchState, setSearchCount } = useSearchFiles();
  const [activeLawFiles, setActiveLawFiles] = useState(new Set()); // Initialize empty, will fill from manifest
  const [isLoading, setIsLoading] = useState(false);
  // Removed showAdvancedSearch state

  // Map context state
  const searchQuery = librarySearchState.query;
  const submittedQuery = librarySearchState.submittedQuery;
  const fileResults = librarySearchState.results;
  const searchAttempted = librarySearchState.attempted;
  const data = librarySearchState.data;

  const setSearchQuery = (val) => setLibrarySearchState(prev => ({ ...prev, query: val }));
  const setSubmittedQuery = (val) => setLibrarySearchState(prev => ({ ...prev, submittedQuery: val }));
  const setFileResults = (val) => setLibrarySearchState(prev => ({ ...prev, results: val }));
  const setSearchAttempted = (val) => setLibrarySearchState(prev => ({ ...prev, attempted: val }));
  const setData = (val) => setLibrarySearchState(prev => ({ ...prev, data: val }));
  const setScrollPosition = (val) => setLibrarySearchState(prev => ({ ...prev, scrollPosition: val }));
  
  const scrollRef = useRef(0);

  // Restore scroll position when results are rendered
  useEffect(() => {
    // Only restore if we have results and a saved position
    if (librarySearchState.scrollPosition > 0 && librarySearchState.results.length > 0) {
      // Use a slightly longer timeout to ensure DOM is painted
      const timer = setTimeout(() => {
        window.scrollTo(0, librarySearchState.scrollPosition);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [librarySearchState.results]);

  // Track scroll using useLayoutEffect to capture position right before unmount/paint changes
  React.useLayoutEffect(() => {
    const handleScroll = () => {
      scrollRef.current = window.scrollY;
    };
    
    // Initialize
    scrollRef.current = window.scrollY;

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      // Save valid scroll position (ignore 0 if we had a value before, to prevent reset on fast unmounts)
      if (scrollRef.current > 0) {
        setScrollPosition(scrollRef.current);
      }
    };
  }, []);

  const handleSearch = () => {
    setSearchAttempted(true);
    setSubmittedQuery(searchQuery);
    setSearchCount(prevCount => prevCount + 1);
  };

  const fetchLawFiles = useCallback(async () => {
    try {
      const response = await fetch(`/Research/manifest.json?v=${new Date().getTime()}`);
      if (!response.ok) {
        throw new Error('Failed to fetch research manifest');
      }
      const fileList = await response.json();
      
      // Auto-select ALL files from Research folder
      const allFiles = new Set(fileList.map(item => item.file || item));
      setActiveLawFiles(allFiles);
    } catch (error) {
      console.error("Could not fetch research files:", error);
    }
  }, []);

  useEffect(() => {
    fetchLawFiles();
  }, [fetchLawFiles]);

  const normalizeText = (text) => {
    // Remove zero-width spaces that might be inserted by iOS Khmer keyboards
    let normalized = text.replace(/\u200B/g, '');
    // Normalize by finding "មាត្រា" followed by one or more spaces and a digit (Arabic or Khmer),
    // and removing the spaces.
    // Arabic digits: 0-9
    // Khmer digits: \u17E0-\u17E9
    return normalized.toLowerCase().replace(/មាត្រា\s+([0-9\u17E0-\u17E9])/g, 'មាត្រា$1');
  };

  useEffect(() => {
    if (!searchAttempted || !submittedQuery) {
      setFileResults([]);
      return;
    }

    // Split query by comma, normalize each term, and filter out empty strings
    const searchTerms = submittedQuery.split(',')
      .map(term => normalizeText(term.trim()))
      .filter(term => term.length > 0);

    // If no valid search terms (e.g. just commas), return
    if (searchTerms.length === 0) {
       setFileResults([]);
       return;
    }

    const results = data.filter(item => {
      const normalizedItemName = normalizeText(item.name);
      // Returns true if the item contains ANY of the search terms
      return searchTerms.some(term => normalizedItemName.includes(term));
    });
    
    setFileResults(results);
  }, [data, submittedQuery, searchAttempted]);

  useEffect(() => {
    const loadFileContent = async () => {
      setIsLoading(true);
      const fileData = [];
      for (const fileName of activeLawFiles) {
        try {
          const response = await fetch(`/Research/${fileName}?v=${new Date().getTime()}`);
          const arrayBuffer = await response.arrayBuffer();
          const decoder = new TextDecoder('utf-8');
          const text = decoder.decode(arrayBuffer);
          const segments = text.split(/(?=<)/);
          segments.forEach((segment, index) => {
            if (segment.trim()) {
              fileData.push({
                id: `${fileName}-segment-${index}`,
                name: segment,
                source: fileName,
              });
            }
          });
        } catch (error) {
          console.error(`Failed to load content for ${fileName}:`, error);
        }
      }
      setData(fileData);
      setIsLoading(false);
    };

    if (activeLawFiles.size > 0) {
      if (data.length === 0) {
        loadFileContent();
      }
    } else {
      if (data.length > 0) setData([]);
    }
  }, [activeLawFiles]);

  return (
    <div className="library-page" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <SearchInterface
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onSearch={handleSearch}
        isLoading={isLoading}
        showAdvancedSearchButton={false}
      />
      <SearchSummary
        searchAttempted={searchAttempted}
        searchQuery={submittedQuery}
        fileResultsCount={fileResults ? fileResults.length : 0}
      />
      <Content
        fileResults={fileResults}
        isLoading={isLoading}
        searchAttempted={searchAttempted}
        searchQuery={submittedQuery}
        showPdfLink={true}
        pdfPath="/Research/"
      />
    </div>
  );
}

export default Research;
